import { useMintNFTMutation } from "@/api/inventory/mint";
import { Button, Group, NumberInput, Paper, Stack, Text } from "@mantine/core";
import { IconReload } from "@tabler/icons-react";
import { useForm, useStore } from "@tanstack/react-form";
import { z } from "zod";

const formSchema = z.object({
	quantity: z.number().min(1, "Quantity must be at least 1"),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	quantity: 1,
};

interface ERC721AMintProps {
	collectionAddress: string;
}

export function ERC721AMint({ collectionAddress }: ERC721AMintProps) {
	const mintMutation = useMintNFTMutation();

	const form = useForm({
		defaultValues: DEFAULT_VALUES,
		validators: {
			onChange: formSchema,
		},
		onSubmit: async ({ value }) => {
			// Mint NFTs
			await mintMutation.mutateAsync({
				collectionAddress,
				quantity: value.quantity,
			});
		},
	});

	const canSubmit = useStore(form.store, (state) => state.canSubmit);
	const isSubmitting = mintMutation.isPending;
	const isSubmitted = mintMutation.isSuccess;

	return (
		<Stack gap="md" p="md" maw={900} mx="auto">
			<Group justify="space-between">
				<Text size="xl" fw={700}>
					Mint NFTs (ERC721A)
				</Text>
				<Group gap="sm">
					<Button
						variant="subtle"
						leftSection={<IconReload size="1.2rem" />}
						onClick={() => {
							form.reset();
							mintMutation.reset();
						}}
					>
						Reset Form
					</Button>
					{isSubmitted ? (
						<Button
							onClick={() => {
								mintMutation.reset();
							}}
						>
							Mint More
						</Button>
					) : (
						<Button
							onClick={() => form.handleSubmit()}
							loading={isSubmitting}
							disabled={!canSubmit || isSubmitting || isSubmitted}
						>
							Mint NFTs
						</Button>
					)}
				</Group>
			</Group>

			<Stack gap="md">
				<form.Field name="quantity">
					{(field) => (
						<NumberInput
							label="Quantity"
							value={field.state.value}
							onChange={(value) => field.handleChange(Number(value))}
							onBlur={field.handleBlur}
							required
							allowDecimal={false}
							min={1}
							step={1}
							placeholder="How many NFTs to mint"
							error={!!field.state.meta.errors[0]}
							disabled={isSubmitting || isSubmitted}
						/>
					)}
				</form.Field>
			</Stack>
		</Stack>
	);
}
